/*
 * File: colors.js
 * Project: our-wave-stories-app
 *
 * Created by Brendan Michaelsen on June 13, 2024 at 9:45 AM
 * Copyright © 2024 Our Wave, Inc. All rights reserved.
 *
 * Last Modified: May 16, 2025 at 9:47 AM
 * Modified By: Brendan Michaelsen
 */

/**
 * Imports
 */

// Constants
import { UI_MODE_OPTIONS } from '../../constants/base.constants';


/**
 * Color Values
 */

export const ColorValues = {

	// Brand
	brandDarkerBlue: '#44566c',
	brandDarkBlue: '#4d708c',
	brandBlue: '#9ebed8',
	brandMediumBlue: '#b2cbdf',
	brandLightBlue: '#c6d8e5',
	brandLighterBlue: '#f0f5f7',
	brandHover: '#f7fbfc',
	brandLightOrangeBackground: '#fffcf5',
	brandHightlightOrange: '#FF8A5B',

	// Primary
	white: '#FFFFFF',
	dark: '#121212',

	// Neutral
	neutralButtonBase: '#747888',
	neutralButtonLighter: '#EEF0F6',
	neutralBackground: '#EFF1F3',
	neutralBackgroundDarker: '#39393B',
	neutralBackgroundLighter: '#efefef',
	neutralHighlight: '#E2E2E2',
	neutralHighlightDarker: '#505054',
	neutralForm: '#DBDFEC',
	neutralText: '#7F8496',
	neutralBorder: 'rgba(74,74,74,.05)',

	// Obfuscation
	obfuscationGrey: '#e7e7e7',
	obfuscationGreyText: '#9f9f9f',

	// Success
	statusSuccessBase: '#01a816',
	statusSuccessLighter: '#EAFFEE',
	statusSuccessDarker: '#04290D',

	// Warning
	statusWarningBase: '#A98E2B',
	statusWarningLighter: '#FFFEED',
	statusWarningDarker: '#302B05',

	// Danger
	statusDangerBase: '#E13737',
	statusDangerLighter: '#FFEBEB',
	statusDangerDarker: '#360404',

	// Info
	statusInfoBase: '#1A4ED4',
	statusInfoLighter: '#E7EDFF',
	statusInfoDarker: '#0B1D4D',

	// Shadows
	navigationShadow: '0px 1px 1px rgb(0 0 0 / 15%)',
	elementShadow: '0px -3px 20px rgba(0, 0, 0, 0.06)',
	elementMediumShadow: '0 15px 35px #31315d1a,0 5px 15px #00000014',
	elementDarkShadow: '0px 4px 16px rgba(0 0 0 / 22%)',
	surfaceShadow: 'rgba(0, 0, 0, 0.12) 0px 1px 1px 0px, rgba(64, 68, 82, 0.16) 0px 0px 0px 1px, rgba(0, 0, 0, 0) 0px 0px 0px 0px, rgba(0, 0, 0, 0) 0px 0px 0px 0px, rgba(64, 68, 82, 0.08) 0px 2px 5px 0px',

	// Brands
	whatsAppBrand: '#25d366',
	facebookBrand: '#0075fa',
	twitterBrand: '#1da1f2',
	linkedinBrand: '#0a66c2',
	redditBrand: '#ff4500',
	pinterestBrand: '#e60019'
};


/**
 * Base Themes
 */

const BaseDarkTheme = (partner) => ({

	// Basic
	white: ColorValues.white,
	dark: ColorValues.dark,

	// Brand
	brandPrimaryBase: ColorValues.brandBlue,
	brandAccent: ColorValues.brandMediumBlue,
	brandHighlight: ColorValues.brandHightlightOrange,

	// Success
	statusSuccessBase: ColorValues.statusSuccessBase,
	statusSuccessLighter: ColorValues.statusSuccessDarker,

	// Warning
	statusWarningBase: ColorValues.statusWarningBase,
	statusWarningLighter: ColorValues.statusWarningDarker,

	// Danger
	statusDangerBase: ColorValues.statusDangerBase,
	statusDangerLighter: ColorValues.statusDangerDarker,

	// Info
	statusInfoBase: ColorValues.statusInfoBase,
	statusInfoLighter: ColorValues.statusInfoDarker,

	// Background
	primaryBackground: ColorValues.dark,
	secondaryBackground: ColorValues.neutralBackgroundDarker,
	tertiaryBackground: ColorValues.neutralBackgroundLighter,
	surfaceBackground: ColorValues.dark,

	// Text
	primaryText: ColorValues.white,
	primarySurfaceText: ColorValues.white,
	secondaryText: ColorValues.neutralText,
	secondarySurfaceText: ColorValues.neutralText,

	// Button
	primaryButton: ColorValues.brandBlue,
	secondaryButton: ColorValues.brandMediumBlue,
	neutralButton: ColorValues.neutralButtonBase,

	// Decoration
	primaryDecoration: ColorValues.white,
	secondaryDecoration: ColorValues.neutralBorder,

	// Borders
	layoutBorder: ColorValues.neutralText,
	navigationBorder: ColorValues.neutralText,

	// Elements
	inactiveElement: ColorValues.neutralText,
	hoverElement: ColorValues.neutralBackgroundDarker,

	// Placeholders
	placeholderPrimary: ColorValues.neutralBackgroundDarker,
	placeholderHighlight: ColorValues.neutralHighlightDarker,

	// Obfuscation
	obfuscationGrey: ColorValues.obfuscationGrey,
	obfuscationGreyText: ColorValues.obfuscationGreyText,

	// Input
	inputBackground: ColorValues.dark,
	inputSecondaryBackground: ColorValues.neutralBackground,
	inputBorder: ColorValues.neutralText,
	inputFocusBorder: ColorValues.neutralButtonBase,
	inputPlaceholder: ColorValues.obfuscationGreyText,

	// Shadows
	navigationShadow: ColorValues.navigationShadow,
	elementShadow: ColorValues.elementShadow,
	elementMediumShadow: ColorValues.elementMediumShadow,
	elementDarkShadow: ColorValues.elementDarkShadow,
	surfaceShadow: ColorValues.surfaceShadow,

	// Set partner theme overrides
	...(partner?.theme?.dark || {}),

	// Font
	headersFontFamily: partner?.fonts?.headers_font_family,
	contentFontFamily: partner?.fonts?.content_font_family,
});

const BaseLightTheme = (partner) => ({

	// Basic
	white: ColorValues.white,
	dark: ColorValues.dark,

	// Brand
	brandPrimaryBase: ColorValues.brandBlue,
	brandAccent: ColorValues.brandMediumBlue,
	brandHighlight: ColorValues.brandHightlightOrange,

	// Success
	statusSuccessBase: ColorValues.statusSuccessBase,
	statusSuccessLighter: ColorValues.statusSuccessLighter,

	// Warning
	statusWarningBase: ColorValues.statusWarningBase,
	statusWarningLighter: ColorValues.statusWarningLighter,

	// Danger
	statusDangerBase: ColorValues.statusDangerBase,
	statusDangerLighter: ColorValues.statusDangerLighter,

	// Info
	statusInfoBase: ColorValues.statusInfoBase,
	statusInfoLighter: ColorValues.statusInfoLighter,

	// Background
	primaryBackground: ColorValues.brandLightOrangeBackground,
	secondaryBackground: ColorValues.brandLighterBlue,
	tertiaryBackground: ColorValues.neutralBackgroundLighter,
	surfaceBackground: ColorValues.white,

	// Text
	primaryText: ColorValues.brandDarkerBlue,
	primarySurfaceText: ColorValues.brandDarkerBlue,
	secondaryText: ColorValues.neutralText,
	secondarySurfaceText: ColorValues.neutralText,

	// Button
	primaryButton: ColorValues.brandDarkerBlue,
	secondaryButton: ColorValues.brandDarkBlue,
	neutralButton: ColorValues.neutralBackgroundLighter,

	// Decoration
	primaryDecoration: ColorValues.brandDarkerBlue,
	secondaryDecoration: ColorValues.brandDarkBlue,
	neutralDecoration: ColorValues.neutralBorder,

	// Borders
	layoutBorder: ColorValues.neutralForm,
	navigationBorder: 'transparent',

	// Elements
	inactiveElement: ColorValues.neutralText,
	hoverElement: ColorValues.brandHover,

	// Placeholders
	placeholderPrimary: ColorValues.neutralBackground,
	placeholderHighlight: ColorValues.neutralHighlight,

	// Obfuscation
	obfuscationGrey: ColorValues.obfuscationGrey,
	obfuscationGreyText: ColorValues.obfuscationGreyText,

	// Input
	inputBackground: ColorValues.white,
	inputSecondaryBackground: ColorValues.neutralBackground,
	inputBorder: ColorValues.neutralForm,
	inputFocusBorder: ColorValues.neutralButtonBase,
	inputPlaceholder: ColorValues.obfuscationGreyText,

	// Shadows
	navigationShadow: ColorValues.navigationShadow,
	elementShadow: ColorValues.elementShadow,
	elementMediumShadow: ColorValues.elementMediumShadow,
	elementDarkShadow: ColorValues.elementDarkShadow,
	surfaceShadow: ColorValues.surfaceShadow,

	// Set partner theme overrides
	...(partner?.theme?.primary || {}),

	// Font
	headersFontFamily: partner?.fonts?.headers_font_family,
	contentFontFamily: partner?.fonts?.content_font_family,
});


/**
 * Themes
 */

export const DarkTheme = (partner) => BaseDarkTheme(partner);

export const LightTheme = (partner) => {
	if (partner?.theme?.primary_base_theme === UI_MODE_OPTIONS.DARK) {
		return BaseDarkTheme(partner);
	}
	return BaseLightTheme(partner);
};
